import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeToggle } from './ThemeToggle';
import { getStartPage } from '../lib/api';
import { readStoredValue, writeStoredValue } from '../lib/safeStorage';
import { blockAnchorId, defaultStartPageBlocks, pickText } from '../startPage/content';
import { StartPageBlocks } from '../startPage/StartPageBlocks';
import type { PublicStartPage, StartPageBlock } from '../startPage/types';

interface LandingPageProps {
  onSignIn: () => void;
  isLoading: boolean;
}

/** The last start page fetched, so a returning visitor sees it at once instead of after the API call. */
const CACHE_KEY = 'quickdial.startPage.v1';

/** How long a first-time visitor waits for the API before getting the built-in page. */
const FETCH_TIMEOUT_MS = 4000;

function blocksOf(page: PublicStartPage): StartPageBlock[] {
  return page.isCustomized ? page.blocks : defaultStartPageBlocks();
}

function readCachedBlocks(): StartPageBlock[] | null {
  const cached = readStoredValue(CACHE_KEY);
  if (!cached) return null;
  try {
    const page = JSON.parse(cached) as PublicStartPage;
    return Array.isArray(page.blocks) ? blocksOf(page) : null;
  } catch {
    return null;
  }
}

/**
 * The start page's blocks: the cached copy (if any) straight away, then the hosting admin's current page.
 * Falls back to the built-in page when the API cannot be reached, so the sign-in button never goes missing.
 */
function useStartPageBlocks(): StartPageBlock[] | null {
  const [blocks, setBlocks] = useState<StartPageBlock[] | null>(readCachedBlocks);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => {
      setBlocks((current) => current ?? defaultStartPageBlocks());
    }, FETCH_TIMEOUT_MS);

    getStartPage(controller.signal)
      .then((page) => {
        setBlocks(blocksOf(page));
        writeStoredValue(CACHE_KEY, JSON.stringify(page));
      })
      .catch(() => {
        if (!controller.signal.aborted) setBlocks((current) => current ?? defaultStartPageBlocks());
      })
      .finally(() => window.clearTimeout(timeout));

    return () => {
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, []);

  return blocks;
}

export function LandingPage({ onSignIn, isLoading }: LandingPageProps) {
  const blocks = useStartPageBlocks();
  return <StartPageLayout blocks={blocks} onSignIn={onSignIn} isLoading={isLoading} />;
}

interface StartPageLayoutProps {
  /** Null while the page is still loading: header and footer only. */
  blocks: StartPageBlock[] | null;
  onSignIn: () => void;
  isLoading: boolean;
  /** Overrides this build's AppLauncher frame origin; the portal preview passes its own. */
  embedOrigin?: string | null;
}

/** The landing page's fixed header and footer around the start-page blocks. */
export function StartPageLayout({ blocks, onSignIn, isLoading, embedOrigin }: StartPageLayoutProps) {
  const { t, i18n } = useTranslation();
  const navBlocks = (blocks ?? [])
    .filter((block) => !block.hidden)
    .map((block) => ({ block, label: pickText(block.navLabel, i18n.language) }))
    .filter((entry): entry is { block: StartPageBlock; label: string } => entry.label !== null);

  return (
    <div className="landing-page">
      {/* Header - matches Exobe template: dark sticky header */}
      <header className="landing-header">
        <div className="landing-header-inner">
          <div className="landing-logo">
            <img src="/exobe-logo.png" alt="Exobe" className="landing-logo-img" />
          </div>

          <nav className="landing-nav" aria-label={t('startPage.navigation')}>
            {navBlocks.map(({ block, label }) => (
              <a key={block.id} href={`#${blockAnchorId(block)}`} className="landing-nav-link">{label}</a>
            ))}
          </nav>

          <div className="landing-header-actions">
            <button
              className="landing-btn landing-btn-primary landing-btn-sm landing-header-signin"
              onClick={onSignIn}
              disabled={isLoading}
            >
              {isLoading ? t('startPage.signingIn') : t('startPage.signIn')}
            </button>
            <ThemeToggle variant="icon" />
          </div>
        </div>
      </header>

      <main className="landing-main" aria-busy={blocks === null}>
        {blocks && (
          <StartPageBlocks
            blocks={blocks}
            onSignIn={onSignIn}
            isLoading={isLoading}
            {...(embedOrigin !== undefined ? { embedOrigin } : {})}
          />
        )}
      </main>

      {/* Footer - matches Exobe template: dark footer with logo + links */}
      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-footer-top">
            <div className="landing-footer-brand">
              <img src="/exobe-logo.png" alt="Exobe" />
              <span>QuickDial</span>
            </div>
            <div className="landing-footer-links">
              <a href="https://exobe.com" target="_blank" rel="noopener noreferrer">
                exobe.com
              </a>
            </div>
          </div>
          <div className="landing-footer-bottom">
            <span className="landing-footer-copyright">
              &copy; {new Date().getFullYear()} Exobe. {t('startPage.rightsReserved')}
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
